import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { DateService } from '@application/time/date.service';
import { UniversalConfig } from '@application/universal_config.service';
import { useTimeSlots } from '@feature/time_slot/use_time_slots';
import type { PhysicalLocationApiModel } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useBookingBasket } from '../../hooks/use_booking_basket';

type LocationType = 'digital' | 'physical';

export interface Location {
	id: number;
	type: LocationType;
	name: string;
	meta: string;
	nextAvailableTime: Dayjs | null;
}

interface AsyncState<T> {
	value: T | null;
	status: 'pending' | 'accessible' | 'error';
}

export function useScreenLocation() {
	const viewBooking = useBooking();
	const universalConfig = useResolve(UniversalConfig);
	const timeSlotsApi = useTimeSlots();
	const bookingBasket = useBookingBasket();
	const analyticService = useResolve(AnalyticService);
	const dateService = useResolve(DateService);
	const [locations, setLocations] = useState<AsyncState<Location[]>>({ value: null, status: 'pending' });

	const getLocation = useCallback(
		(locationId: number): PhysicalLocationApiModel | null =>
			viewBooking.flowData.status === 'accessible'
				? (viewBooking.flowData.value.physicalLocations.find((location) => location.Id === locationId) ?? null)
				: null,
		[viewBooking.flowData],
	);

	const selectLocation = useCallback(
		async (location: Location) => {
			await bookingBasket.setBasket({ currentDate: null });
			if (location.type === 'digital') viewBooking.goToScreen(BookingScreen.TimeSlot, { locationType: 'digital' });
			else viewBooking.goToScreen(BookingScreen.TimeSlot, { locationType: 'physical', locationId: location.id });
		},
		[bookingBasket.setBasket, viewBooking.goToScreen],
	);

	useEffect(() => {
		analyticService.trackPageView({ customTitle: 'Booking: Location' });
		bookingBasket.unlockTimeSlot();

		const allowedToBeHere = () => {
			if (viewBooking.flowData.status !== 'accessible') return false;
			const flowData = viewBooking.flowData.value;
			if (bookingBasket.getBasket().respondantType == null) return false;
			return !(
				flowData.useReasonForVisits &&
				viewBooking.getFilteredReasonForVisits().length > 0 &&
				!viewBooking.getSelectedReasonForVisit()
			);
		};

		(async () => {
			if (!allowedToBeHere()) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Location');
				viewBooking.goToScreen(BookingScreen.ReasonForVisit, { replace: true });
				return;
			}

			const flowSettings = viewBooking.getFlowSettings();
			if (flowSettings.locationId) {
				analyticService.trackEvent(
					AnalyticCategories.Booking,
					ViewBookingAnalyticActions.AutoSetLocation,
					`${flowSettings.locationId}`,
				);
				viewBooking.goToScreen(BookingScreen.TimeSlot, {
					replace: true,
					locationType: 'physical',
					locationId: flowSettings.locationId,
					autoForwardedFromLocation: true,
				});
				return;
			}

			if (flowSettings.locationType === 'digital') {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetLocation, 'Digital');
				viewBooking.goToScreen(BookingScreen.TimeSlot, {
					replace: true,
					locationType: 'digital',
					autoForwardedFromLocation: true,
				});
				return;
			}

			let fromDate: Dayjs | null = flowSettings.fromDate;
			let toDate: Dayjs | null = flowSettings.toDate;
			if (
				flowSettings.searchContext?.FirstSearchDate == null &&
				flowSettings.searchContext?.RelativeStart == null &&
				flowSettings.fromDate == null
			) {
				fromDate = dateService.newDate();
			}
			if (
				flowSettings.searchContext?.LastSearchDate == null &&
				flowSettings.searchContext?.RelativeEnd == null &&
				flowSettings.toDate == null
			) {
				toDate = (fromDate ?? dateService.newDate()).add(1, 'year');
			}

			let availability;
			try {
				availability = await timeSlotsApi.getAvailableTimeSlots(universalConfig.selectedUnitId, {
					searchContextId: flowSettings.searchContextId ?? undefined,
					conditionId: bookingBasket.getBasket().reasonForVisit ?? undefined,
					fromDate: fromDate?.format('YYYY-MM-DD'),
					toDate: toDate?.format('YYYY-MM-DD'),
					filterableUnitId: flowSettings.filterUnitId ?? undefined,
					resourceId: flowSettings.filterResourceId ?? undefined,
					specialityId: flowSettings.filterSpecialityId ?? undefined,
					additionalParameters: flowSettings.additionalParameters ?? undefined,
				});
			} catch (error) {
				analyticService.trackEvent(
					AnalyticCategories.Booking,
					ViewBookingAnalyticActions.ErrorFetchingAvailability,
					error instanceof Error ? error.message : String(error),
				);
				viewBooking.goToScreen(BookingScreen.Error);
				return;
			}

			const nextLocations: Location[] = [];
			if (availability.Digital.FirstAvailableUpcomingTime && flowSettings.locationType !== 'physical') {
				nextLocations.push({
					id: -1,
					type: 'digital',
					name: I18nAndL10n.translate('View.Booking.ScreenLocation.Digital.Title'),
					meta: I18nAndL10n.translate('View.Booking.ScreenLocation.Digital.Description'),
					nextAvailableTime: dateService.newDate(availability.Digital.FirstAvailableUpcomingTime),
				});
			}

			if (viewBooking.flowData.status === 'accessible') {
				viewBooking.flowData.value.physicalLocations.map((location) => {
					const firstAvailableUpcomingTime = availability.PhysicalLocations[location.Id]?.FirstAvailableUpcomingTime;
					if (firstAvailableUpcomingTime) {
						nextLocations.push({
							id: location.Id,
							type: 'physical',
							name: location.Name ?? '',
							meta: `${location.Address}, ${location.PostalCode} ${location.City}`,
							nextAvailableTime: dateService.newDate(firstAvailableUpcomingTime),
						});
					}
				});
			}

			if (
				nextLocations.find((location) => location.type === 'digital') &&
				nextLocations.find((location) => location.type === 'physical') == null
			) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.OnlyDigital);
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetLocation, 'Digital');
				viewBooking.goToScreen(BookingScreen.TimeSlot, {
					replace: true,
					locationType: 'digital',
					autoForwardedFromLocation: true,
				});
				return;
			}

			setLocations({ value: nextLocations, status: 'accessible' });
		})();
	}, [
		analyticService,
		bookingBasket.unlockTimeSlot,
		bookingBasket.getBasket,
		dateService,
		timeSlotsApi,
		universalConfig.selectedUnitId,
		viewBooking.flowData,
		viewBooking.getFilteredReasonForVisits,
		viewBooking.getSelectedReasonForVisit,
		viewBooking.getFlowSettings,
		viewBooking.goToScreen,
	]);

	return { locations, getLocation, selectLocation };
}
